/**
 * Narrows the campaign identifiers the TH app puts on the handoff URL to the
 * `SingularAttributionInput` the gateway accepts.
 *
 * Nothing is renamed: a parameter is spelled the same on the URL, in the
 * GraphQL input, and on the row. The app forwards a superset, so the narrowing
 * lives here — it is the tier that redeploys freely, whereas widening the app
 * costs a store release.
 *
 * Keys the app sends that are deliberately dropped today: `cl`, `paffn`,
 * `utm_id`, `utm_audience`, `utm_segment`.
 *
 * Nothing here truncates. These values are untrusted, and the gateway is the
 * trust boundary that bounds them.
 */
import type { ISingularAttributionInput } from 'generated/graphql-gateway';

/** Observed by the app rather than read off the link, so `th_`-prefixed to stop a link param forging them. */
const CAPTURED_AT_PARAM = 'th_captured_at';
const DESTINATION_PARAM = 'th_destination';

/**
 * Derived from the gateway input rather than written out, so a key the gateway
 * does not accept — or one it accepts and this list forgets — fails the build.
 * The two `th_` params are excluded because each needs converting first.
 */
type VerbatimParamKey = Exclude<
  keyof ISingularAttributionInput,
  typeof CAPTURED_AT_PARAM | typeof DESTINATION_PARAM
>;

/**
 * Forwarded as-is. `pcid`/`pcn` and friends pair an opaque ad-platform id with
 * a human-readable name, and both are carried because the ids mean nothing on
 * their own. `wpsrc` is set by whoever trafficked the link rather than resolved
 * by Singular, so it travels under its own key rather than an interpreted one.
 */
const VERBATIM_PARAMS: Record<VerbatimParamKey, true> = {
  pcid: true,
  pcn: true,
  pscid: true,
  pscn: true,
  pcrid: true,
  pcrn: true,
  wpsrc: true,
  utm_source: true,
  utm_medium: true,
  utm_campaign: true,
  utm_term: true,
  utm_content: true,
  passthrough: true,
};

const VERBATIM_PARAM_KEYS = Object.keys(VERBATIM_PARAMS) as VerbatimParamKey[];

/**
 * Reduces the campaign's deep link to just where it points. The live links set
 * `_forward_params=1`, so the raw `_dl` arrives with the campaign's own
 * parameters appended — left as-is, `WHERE th_destination = 'timhortons://…'`
 * would match nothing and every identifier would be duplicated into it.
 */
function toDestination(raw: string | undefined): string | undefined {
  return raw?.split(/[?#]/)[0] || undefined;
}

/** Raw parameter names, so what crosses the OIDC round-trip stays a faithful copy of the URL. */
export type SingularAttribution = Partial<
  Record<VerbatimParamKey | typeof CAPTURED_AT_PARAM | typeof DESTINATION_PARAM, string>
>;

/**
 * Lifts the campaign parameters off the current URL. Also covers the web-origin
 * journey for free: a guest clicking the same ad on desktop lands here with the
 * parameters already present and never touches the app.
 */
export function readSingularAttribution(searchParams: URLSearchParams): SingularAttribution {
  const attribution: SingularAttribution = {};

  for (const key of [...VERBATIM_PARAM_KEYS, CAPTURED_AT_PARAM, DESTINATION_PARAM] as const) {
    const value = searchParams.get(key)?.trim();
    if (value) {
      attribution[key] = value;
    }
  }

  return attribution;
}

/**
 * Maps to the gateway input, or `undefined` when nothing was captured — so an
 * organic attempt omits the argument entirely and its request is identical to
 * one sent before this existed.
 */
export function toSingularAttributionInput(
  attribution: SingularAttribution | undefined
): ISingularAttributionInput | undefined {
  if (!attribution) {
    return undefined;
  }

  const input: Record<string, string | number> = {};

  for (const key of VERBATIM_PARAM_KEYS) {
    const value = attribution[key];
    if (value) {
      input[key] = value;
    }
  }

  // Converted here rather than on read, so a malformed value is dropped at the
  // one boundary that cares about its type. A missing click time is more honest
  // than a wrong one, which would skew every latency measurement.
  const capturedAt = Number(attribution[CAPTURED_AT_PARAM]);
  if (Number.isFinite(capturedAt) && capturedAt > 0) {
    input[CAPTURED_AT_PARAM] = capturedAt;
  }

  const destination = toDestination(attribution[DESTINATION_PARAM]);
  if (destination) {
    input[DESTINATION_PARAM] = destination;
  }

  return Object.keys(input).length > 0 ? (input as ISingularAttributionInput) : undefined;
}
